import { getImageUrl, getVideoUrl } from '@/lib/cdn';

export type ProductTabId = 'sales' | 'marketing';

/** Tab-level product demo reels — replace hero + discover imagery while playing in modal */
export const HOME_TAB_DEMO_VIDEOS: Record<ProductTabId, string> = {
  sales: getVideoUrl('sales-demo.mp4'),
  marketing: getVideoUrl('marketing-demo.mp4'),
};

export interface ProductFeature {
  icon: 'signal' | 'route' | 'learn' | 'chat' | 'target' | 'rank' | 'page' | 'enrich' | 'call' | 'email';
  title: string;
  description: string;
}

export interface DiscoverCard {
  title: string;
  description: string;
  image: string;
  /** Mobile-only override of `image`. Falls back to `image` when absent. */
  mobileImage?: string;
}

export interface ProductModalContent {
  /** Overrides card title in modal header when a longer/catchier headline is needed */
  modalTitle?: string;
  /** Product screenshot shown in the modal body — distinct from the bento card thumbnail */
  modalImage?: string;
  /** Mobile-only override of `modalImage`. Falls back to `modalImage` (with `modalImagePosition` crop) when absent. */
  modalImageMobile?: string;
  /** Tailwind object-position class for the modal hero crop on mobile when no `modalImageMobile` is set. Defaults to 'object-center-top'. */
  modalImagePosition?: string;
  description: string;
  bullets: string[];
  /** Primary header CTA destination — label is always "Explore playbook" in the modal */
  ctaHref: string;
  watchVideoUrl?: string;
  features: [ProductFeature, ProductFeature, ProductFeature];
  discoverCards: [DiscoverCard, DiscoverCard, DiscoverCard];
  testimonial: { quote: string; name: string; role: string; company: string };
  footerCtaLabel: string;
}

export interface HomeProductCard {
  title: string;
  /** One plain sentence on what this agent does, shown under the title. */
  blurb?: string;
  href: string;
  image: string;
  /** Mobile-only override of `image`. When set, used below the sm breakpoint and `image` is used above it. */
  mobileImage?: string;
  imageAlt: string;
  modal: ProductModalContent;
}

export interface HomeProductTab {
  id: ProductTabId;
  label: string;
  headlineBefore: string;
  headlineAccent: string;
  headlineAfter: string;
  primaryRow: [HomeProductCard, HomeProductCard];
  /** Sales tab: three small cards below the primary row */
  secondaryRow?: [HomeProductCard, HomeProductCard, HomeProductCard];
  /** Marketing tab: single full-width card below the primary row */
  wideRow?: HomeProductCard;
}

const DAILY_PLAYBOOK_TESTIMONIAL = {
  quote:
    "Leads used to sit in a queue while we figured out who owned them. The auto-assignment and routing means every inbound lands with the right rep instantly. No more 'who's got this one?' in Slack. Speed-to-lead stopped being our weak spot.",
  name: 'Ashley Samuels',
  role: 'VP Marketing',
  company: 'Kajeet',
};

const VISITOR_ID_TESTIMONIAL = {
  quote:
    'Selling to hardware engineers means generic outreach gets ignored instantly. MarketBetter pulls in enough real context that every message reads like I spent twenty minutes researching the account, at scale. My reply rates roughly doubled.',
  name: 'Taya Wiggins',
  role: 'SDR',
  company: 'AllSpice',
};

const ENRICHMENT_TESTIMONIAL = {
  quote:
    "We sell into a very specific set of government and nonprofit buyers, so a sloppy list wastes everyone's time. Building target lists by exact criteria with the AI gets me clean, precise account lists in minutes. My team spends their time selling, not scrubbing data.",
  name: 'Hannah Burke',
  role: 'CRO',
  company: 'AidKit',
};

const EMAIL_OUTREACH_TESTIMONIAL = {
  quote:
    "I hit my number for the year in a single quarter. The cold email engine let me run personalized sequences at a volume I couldn't touch before. Same effort, a fraction of the manual work, and the replies actually landed.",
  name: 'Felix Morales',
  role: 'SDR',
  company: 'Hologram',
};

const COLD_CALLS_TESTIMONIAL = {
  quote:
    'The dialer keeps my reps in conversations instead of dialing dead numbers, but the AI analysis is the real unlock. I can see which talk tracks move deals and coach off actual call patterns instead of hunches. My ramp time for new reps dropped noticeably.',
  name: 'Eddie Meyersick',
  role: 'VP, Sales',
  company: 'Vutility',
};

const CHATBOT_TESTIMONIAL = {
  quote:
    "Half our pipeline used to walk off the site unidentified. MarketBetter's chatbot now qualifies health-system visitors in real time and routes them straight to my reps. We're booking meetings with people who'd have been anonymous traffic a quarter ago.",
  name: 'Charlie Evans',
  role: 'SDR Manager',
  company: 'Healthcare IT Leaders',
};

const AI_SEO_TESTIMONIAL = {
  quote:
    'We sell into a niche where the right hospital IT leaders are searching very specific terms. The AI SEO insights showed us exactly which intent topics we were invisible for. We reprioritized our content in a week and started ranking for the searches that actually convert.',
  name: 'Courtney Driscoll',
  role: 'VP Marketing',
  company: 'Healthcare IT Leaders',
};

const MARKETING_CHATBOT_MODAL: ProductModalContent = {
  description:
    'Drop in a tag and the bot starts qualifying visitors against your ICP, booking meetings on your calendar, and pulling reps in on Slack when it matters. Your website is working 24/7 for you',
  modalImage: getImageUrl('home/modal/marketing-chatbot-modal-v2.png'),
  bullets: [
    'Trained on your site, docs, and PDFs in minutes',
    'Qualifies every visitor against your ICP before booking',
    'Books meetings on the right rep\'s calendar automatically',
    'Hands off to Slack or Teams with the full transcript and intent',
  ],
  ctaHref: '/products/ai-chatbot',
  watchVideoUrl: getVideoUrl('testimonial_5_new.mp4'),
  features: [
    {
      icon: 'chat',
      title: 'Trained on your site in minutes',
      description:
        'Point it at your URL. The crawler ingests your pages, docs, and pricing, and the bot is answering live prospect questions before lunch — grounded in your real product, not generic web text.',
    },
    {
      icon: 'target',
      title: 'Qualifies against your ICP, books on your calendar',
      description:
        'Define the fit filter — size, industry, intent signals. The chat AI only books meetings that match, and pushes the rest into a nurture so nothing leaks out.',
    },
    {
      icon: 'route',
      title: 'Hands off to Slack or Teams mid-thread',
      description:
        'When it hits a question worth a human — or a buyer worth jumping on — the bot pulls in the right rep on Slack or Teams with the full transcript and detected intent attached.',
    },
  ],
  discoverCards: [
    {
      title: 'Control how your chatbot speaks',
      description: 'Choose ai models and set tones like friendly, formal, or engaging to match your brand voice.',
      image: getImageUrl('home/modal/marketing-chatbot-discover-1-v2.png'),
    },
    {
      title: 'Welcome visitors with a video bot',
      description: 'Add a friendly video intro that explains your product and makes your site feel human.',
      image: getImageUrl('home/modal/marketing-chatbot-discover-2-v2.png'),
    },
    {
      title: 'Slack & Teams, natively',
      description:
        'Reps reply in-thread without leaving Slack. Route by topic, account owner, or time zone. Transcripts pipe to whoever owns the deal in your CRM — no copy-paste, no tab-switching.',
      image: getImageUrl('home/modal/marketing-chatbot-discover-3-v2.png'),
    },
  ],
  testimonial: CHATBOT_TESTIMONIAL,
  footerCtaLabel: 'Get started with Email Automation',
};

const MARKETING_AI_SEO_MODAL: ProductModalContent = {
  description:
    'Connect Google Search Console and we\'ll show you exactly which prompts ChatGPT, Perplexity, Claude, and Gemini surface you for, who\'s cited instead, and the content gaps that will close fastest.',
  modalImage: getImageUrl('home/modal/marketing-ai-seo-modal-v2.png'),
  bullets: [
    'Tracks visibility across ChatGPT, Perplexity, Claude, and Gemini',
    'Maps every co-citation — see exactly which competitors AI lists alongside you',
    'Ranks 180+ content opportunities by strategy: New pillar, Refresh, CTR rescue, Consolidate, Defend',
    'Closes the loop: shipped → lifted → citations gained, on a 90-day window',
  ],
  ctaHref: '/products/ai-seo',
  watchVideoUrl: getVideoUrl('testimonial_5_new.mp4'),
  features: [
    {
      icon: 'rank',
      title: 'Score every content opportunity',
      description:
        'Every cluster gets a strategy tag — New pillar, Refresh, CTR rescue, Consolidate, Defend, Fold in — and an opportunity score blending GSC impressions, current rank, citation gap, and prompt intent. Your team works the top of the queue, not their gut.',
    },
    {
      icon: 'signal',
      title: 'See who\'s eating your citations',
      description:
        'AI Engine References shows every domain AI cites in your market — yours, your competitors\', and the third-party media outranking both. Filter for "competitor-only" to surface the prompts you\'re invisible on right now.',
    },
    {
      icon: 'target',
      title: 'Track every win to revenue',
      description:
        'Ship a page, watch the lift. The dashboard reconciles published opportunities against citation and ranking deltas over 90 days, so you know which strategies actually move the needle — and which to retire.',
    },
  ],
  discoverCards: [
    {
      title: 'AI Engine References, scored by influence',
      description:
        'Every cited domain ranked 0-100 on influence, grouped by type — Media, Products, Tools, Docs. The "not co-cited" filter is your shortlist of easy wins.',
      image: getImageUrl('home/modal/marketing-ai-seo-discover-1-v2.png'),
    },
    {
      title: 'Briefs auto-drafted from your GSC data',
      description:
        'Each opportunity ships with a brief: target prompt, page type, anchor sources, internal link recommendations. Move it from New → Brief ready → Article ready → Published without leaving the queue.',
      image: getImageUrl('home/modal/marketing-ai-seo-discover-2-v2.png'),
    },
    {
      title: 'Per-prompt visibility timelines',
      description:
        'Watch rank, citation count, and competitor displacement evolve prompt-by-prompt over 30/90/180 days. Spot the prompts trending the wrong way before they fall off.',
      image: getImageUrl('home/modal/marketing-ai-seo-discover-3-v2.png'),
    },
  ],
  testimonial: AI_SEO_TESTIMONIAL,
  footerCtaLabel: 'Get started with Email Automation',
};

const VISITOR_ID_MODAL: ProductModalContent = {
  description:
    'Anonymous traffic → named companies, ranked by fit and intent. Hot accounts hit the playbook the same hour they land.',
  modalImage: getImageUrl('home/modal/visitor-id-modal-v2.png'),
  modalImageMobile: getImageUrl('home/modal/mobile/visitor-id-modal-v2.png'),
  bullets: [
    'Resolves 70%+ of US B2B traffic',
    'Per-page heatmap of buying-intent signals',
    'Hot-account alerts in Slack',
    'Routes the right rep automatically',
  ],
  ctaHref: '/products/visitor-identification',
  watchVideoUrl: getVideoUrl('testimonial_4_new.mp4'),
  features: [
    {
      icon: 'target',
      title: 'Resolve anonymous traffic',
      description:
        'Reverse-IP plus a graph that learns your real visitors. We resolve 70%+ of US B2B traffic, with confidence scoring on every match.',
    },
    {
      icon: 'signal',
      title: 'Spot intent in the heatmap',
      description:
        'Pricing page twice this week + a careers visit + an integration click is a different signal than a homepage bounce. We score the difference.',
    },
    {
      icon: 'route',
      title: 'Alert before they leave',
      description:
        'Hot accounts ping Slack the same hour they land. Reps reach out while the buyer is still in the tab.',
    },
  ],
  discoverCards: [
    {
      title: 'Know who visited, and when',
      description:
        'Identify visitors, track every page they view, and uncover buying intent before they ever fill a form.',
      image: getImageUrl('home/modal/visitor-id-discover-1-v2.png'),
      mobileImage: getImageUrl('home/modal/mobile/visitor-id-discover-1-v2.png'),
    },
    {
      title: 'Real-Time Intent Signals',
      description: 'See which accounts are on your website right now and what pages they\'re viewing.',
      image: getImageUrl('home/modal/visitor-id-discover-2-v2.png'),
      mobileImage: getImageUrl('home/modal/mobile/visitor-id-discover-2-v2.png'),
    },
    {
      title: 'Know every detail before they convert',
      description:
        'Reveal company details, industry, employee count, and website activity from your visitor traffic.',
      image: getImageUrl('home/modal/visitor-id-discover-3-v2.png'),
      mobileImage: getImageUrl('home/modal/mobile/visitor-id-discover-3-v2.png'),
    },
  ],
  testimonial: VISITOR_ID_TESTIMONIAL,
  footerCtaLabel: 'Get started with Visitor ID',
};

export const HOME_PRODUCT_TABS: Record<ProductTabId, HomeProductTab> = {
  sales: {
    id: 'sales',
    label: 'Sales',
    headlineBefore: 'Your target accounts deserve ',
    headlineAccent: 'more',
    headlineAfter: ' than a place on a list',
    primaryRow: [
      {
        title: 'Signals agent: ranks every account by intent, every morning',
        blurb: 'Scores every visit, hire and mention, routes it to the right rep and explains why now.',
        href: '/products/daily-playbook',
        image: getImageUrl('home/lp/daily-playbook-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/daily-playbook-v2.png'),
        imageAlt: 'Daily SDR playbook ranked by buyer intent',
        modal: {
          description:
            'Every signal — visits, jobs, hires, funding — collapses into one ranked task list. Reps stop choosing what to work on and just work.',
          modalImage: getImageUrl('home/modal/daily-playbook-modal-v2.png'),
          modalImageMobile: getImageUrl('home/modal/mobile/daily-playbook-modal-v2.png'),
          bullets: [
            'Click-to-call from any task — no app switch',
            'AI suggested openers from live account signals',
            'Live transcript + sentiment',
            'Auto-logs to Salesforce or HubSpot',
          ],
          ctaHref: '/products/daily-playbook',
          watchVideoUrl: getVideoUrl('testimonial_2_new.mp4'),
          features: [
            {
              icon: 'signal',
              title: 'Score every signal',
              description:
                'Each visit, hire, or funding event gets a fit + intent score. The blended ranking is what the rep sees first.',
            },
            {
              icon: 'route',
              title: 'Route to the right rep',
              description:
                'Territory, segment, language, and prior owner are all considered. Tasks land in one queue per rep, never two.',
            },
            {
              icon: 'learn',
              title: 'Learn from every outcome',
              description:
                'Booked, no-show, closed-won — the model retrains nightly so tomorrow\'s queue reflects what\'s actually working.',
            },
          ],
          discoverCards: [
            {
              title: 'Multi-Channel Actions',
              description: 'Call, email, and LinkedIn actions from one screen. No tab switching.',
              image: getImageUrl('home/modal/daily-playbook-discover-1-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/daily-playbook-discover-1-v2.png'),
            },
            {
              title: 'Build Professional Presentations 10x Faster',
              description: 'Generate complete decks or one-pagers with AI and deliver polished presentations in minutes instead of hours.',
              image: getImageUrl('home/modal/daily-playbook-discover-2-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/daily-playbook-discover-2-v2.png'),
            },
            {
              title: 'Refine your workflows anytime',
              description: 'Easily tweak content, tone, and messaging at any stage to match your goals perfectly.',
              image: getImageUrl('home/modal/daily-playbook-discover-3-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/daily-playbook-discover-3-v2.png'),
            },
          ],
          testimonial: DAILY_PLAYBOOK_TESTIMONIAL,
          footerCtaLabel: 'Get started with Daily SDR Playbook',
        },
      },
      {
        title: 'Visitor ID agent: names the companies on your site',
        blurb: 'Turns anonymous traffic into named companies and people, with the pages they read.',
        href: '/products/visitor-identification',
        image: getImageUrl('home/lp/visitor-id-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/visitor-id-v2.png'),
        imageAlt: 'Website visitor identification dashboard',
        modal: VISITOR_ID_MODAL,
      },
    ],
    secondaryRow: [
      {
        title: 'Research agent: builds and enriches lists from a prompt',
        blurb: 'Describe the accounts you want. It finds them, finds the people and answers your questions about each one.',
        href: '/products/enrichment',
        image: getImageUrl('home/lp/enrichment-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/enrichment-v2.png'),
        imageAlt: 'Build and enrich prospect lists with AI',
        modal: {
          modalTitle: 'Just ask. MarketBetter does the research.',
          description:
            'Build audiences, surface buying signals, and enrich contacts with verified data — all from one chat.',
          modalImage: getImageUrl('home/modal/enrichment-modal-v2.png'),
          modalImageMobile: getImageUrl('home/modal/mobile/enrichment-modal-v2.png'),
          bullets: [
            'Find your ideal buyers by description, not filters',
            'Enrich contacts with verified emails and phones',
            'Surface website visitors showing intent',
            'Works on top of your CRM — no migration',
          ],
          ctaHref: '/products/enrichment',
          watchVideoUrl: getVideoUrl('testimonial_2_new.mp4'),
          features: [
            {
              icon: 'enrich',
              title: 'Describe your audience',
              description:
                '"VPs of Marketing at SaaS companies." MarketBetter finds them.',
            },
            {
              icon: 'signal',
              title: 'Enrich on demand',
              description:
                'Fill in verified emails and phone numbers, one row or all at once.',
            },
            {
              icon: 'route',
              title: 'Act on Enrich',
              description:
                'Use exa agents to add columns and enrich using AI agents.',
            },
          ],
          discoverCards: [
            {
              title: 'Add AI Columns',
              description: 'Add dynamic columns and use AI agents to enrich.',
              image: getImageUrl('home/modal/enrichment-discover-1-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/enrichment-discover-1-v2.png'),
            },
            {
              title: 'Chat with MB',
              description: 'Create lists by chatting with our AI agent.',
              image: getImageUrl('home/modal/enrichment-discover-2-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/enrichment-discover-2-v2.png'),
            },
            {
              title: 'Enrich on the fly',
              description: 'Enrich email, phone numbers and LinkedIn using waterfall providers.',
              image: getImageUrl('home/modal/enrichment-discover-3-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/enrichment-discover-3-v2.png'),
            },
          ],
          testimonial: ENRICHMENT_TESTIMONIAL,
          footerCtaLabel: 'Get started with Enrichment',
        },
      },
      {
        title: 'Outreach agent: writes email and LinkedIn from real signals',
        blurb: 'Drafts every message from the account’s research and what it just did, in your rep’s voice.',
        href: '/products/emails',
        image: getImageUrl('home/lp/email-outreach-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/email-outreach-v2.png'),
        imageAlt: 'Email outreach drafted from signal context',
        modal: {
          description:
            'Every email is grounded in what the account just did — never the generic LinkedIn opener. Reps review one line and send.',
          modalImage: getImageUrl('home/modal/email-outreach-modal-v2.png'),
          modalImageMobile: getImageUrl('home/modal/mobile/email-outreach-modal-v2.png'),
          bullets: [
            'AI drafts off live signal evidence',
            'Citations show which signals it used',
            'Per-rep voice match',
            'Sequences pause on reply or website visit',
          ],
          ctaHref: '/products/emails',
          watchVideoUrl: getVideoUrl('testimonial_4_new.mp4'),
          features: [
            {
              icon: 'email',
              title: 'Drafts that cite their sources',
              description:
                'Every draft shows which signals it used — the pricing visit, the new hire, the funding round. Reps verify in seconds.',
            },
            {
              icon: 'signal',
              title: 'Sounds like the rep, not a bot',
              description:
                'Voice-match per rep, learned from sent mail. New SDRs ramp in days because their drafts already sound like them.',
            },
            {
              icon: 'learn',
              title: 'Sequences that listen',
              description:
                'Reply, click, or return-visit pauses the sequence and bumps the account in the playbook. No double-emailing buyers.',
            },
          ],
          discoverCards: [
            {
              title: 'Start with the right list from any source',
              description: 'Import contacts from CRM or CSV, build audiences from website visitors, & enrich automatically before you hit send.',
              image: getImageUrl('home/modal/email-outreach-discover-1-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/email-outreach-discover-1-v2.png'),
            },
            {
              title: '20+ AI agents make personalization scalable',
              description: 'Agents gather context from company sites and market intel to generate snippets your reps can trust.',
              image: getImageUrl('home/modal/email-outreach-discover-2-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/email-outreach-discover-2-v2.png'),
            },
            {
              title: 'Unified inbox for replies',
              description: 'Manage replies, & trigger follow-ups so you never miss the moment a buyer engages.',
              image: getImageUrl('home/modal/email-outreach-discover-3-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/email-outreach-discover-3-v2.png'),
            },
          ],
          testimonial: EMAIL_OUTREACH_TESTIMONIAL,
          footerCtaLabel: 'Get started with Email Outreach',
        },
      },
      {
        title: 'Call prep agent: briefs every call before you dial',
        blurb: 'Who to call, why now and what to say, queued into the dialer with the script ready.',
        href: '/products/cold-calls',
        image: getImageUrl('home/lp/cold-calls-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/cold-calls-v2.png'),
        imageAlt: 'Smart dialer triggered by intent signals',
        modal: {
          description:
            'A call is a task, not a guess. The dialer opens with a suggested opener, a one-screen signal history, and a transcript that writes itself back to the CRM.',
          modalImage: getImageUrl('home/modal/cold-calls-modal-v2.png'),
          modalImageMobile: getImageUrl('home/modal/mobile/cold-calls-modal-v2.png'),
          bullets: [
            'Click-to-call from any task — no app switch',
            'AI suggested openers from live account signals',
            'Live transcript + sentiment',
            'Auto-logs to Salesforce or HubSpot',
          ],
          ctaHref: '/products/cold-calls',
          watchVideoUrl: getVideoUrl('testimonial_6_new.mp4'),
          features: [
            {
              icon: 'call',
              title: 'One-click connect',
              description:
                'Local presence, parallel dialing, and voicemail drop. Reps spend their time talking, not waiting on dials.',
            },
            {
              icon: 'signal',
              title: 'AI opener per call',
              description:
                'The first line is grounded in the account\'s last 7 days of activity. No more "saw your LinkedIn" cold opens.',
            },
            {
              icon: 'learn',
              title: 'CRM-sync transcripts',
              description:
                'Every call lands in Salesforce or HubSpot with sentiment, next steps, and the relevant signal evidence attached.',
            },
          ],
          discoverCards: [
            {
              title: 'Every call ends with clear next steps',
              description: 'MarketBetter scores calls and extracts summaries, so teams move faster and coach with proof.',
              image: getImageUrl('home/modal/cold-calls-discover-1-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/cold-calls-discover-1-v2.png'),
            },
            {
              title: "Don't call cold. Call warmed accounts",
              description: 'MarketBetter sequences the warm-up across channels, then puts the best calls in your queue.',
              image: getImageUrl('home/modal/cold-calls-discover-2-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/cold-calls-discover-2-v2.png'),
            },
            {
              title: 'Make calls where reps already work: Salesforce',
              description: 'Click-to-dial from Leads/Contacts, keep context, and log outcomes back automatically.',
              image: getImageUrl('home/modal/cold-calls-discover-3-v2.png'),
              mobileImage: getImageUrl('home/modal/mobile/cold-calls-discover-3-v2.png'),
            },
          ],
          testimonial: COLD_CALLS_TESTIMONIAL,
          footerCtaLabel: 'Get started with Smart Dialer',
        },
      },
    ],
  },
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    headlineBefore: 'Marketing tools that run\n',
    headlineAccent: 'themselves',
    headlineAfter: '',
    primaryRow: [
      {
        title: 'An AI chatbot that qualifies leads while you sleep',
        href: '/products/ai-chatbot',
        image: getImageUrl('home/lp/marketing-chatbot-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/marketing-chatbot-v2.png'),
        imageAlt: 'AI chatbot qualifying inbound leads',
        modal: MARKETING_CHATBOT_MODAL,
      },
      {
        title: 'Identify every company on your site',
        href: '/products/visitor-identification',
        image: getImageUrl('home/lp/visitor-id-v2.png'),
        mobileImage: getImageUrl('home/lp/mobile/visitor-id-v2.png'),
        imageAlt: 'Website visitor identification dashboard',
        modal: VISITOR_ID_MODAL,
      },
    ],
    wideRow: {
      title: 'See every prompt AI cites you for — and win the ones it doesn\'t',
      href: '/products/ai-seo',
      image: getImageUrl('home/lp/marketing-ai-seo-v2.png'),
      mobileImage: getImageUrl('home/lp/mobile/marketing-ai-seo-v2.png'),
      imageAlt: 'AI search visibility dashboard with visibility, mentions, and citation metrics',
      modal: MARKETING_AI_SEO_MODAL,
    },
  },
};
